import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import TablePagination from '@mui/material/TablePagination';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import {
  Search,
  X,
  RefreshCcw,
  RefreshCw,
  Power,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Building2 as ObBusinessLogoIcon
} from 'lucide-react';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import api from '../services/api';
import { toast } from 'react-toastify';

const StyledCard = styled(Card)({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
});

export default function OpenBankingBusinessRegistry() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [enabledFilter, setEnabledFilter] = useState('all');
  const [autoRenewFilter, setAutoRenewFilter] = useState('all');
  const [hasAccountsFilter, setHasAccountsFilter] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [editingRow, setEditingRow] = useState(null);
  const [form, setForm] = useState({
    fiscal_id: '',
    business_name: '',
    email: '',
    enabled: true,
    auto_renew_subscription: false
  });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  const fetchData = async (term = '') => {
    setLoading(true);
    try {
      const res = await api.get('/open-banking/business-registries', {
        params: term ? { q: term } : {}
      });
      setRows(res.data || []);
    } catch (error) {
      console.error('Error fetching business registries', error);
      toast.error('Errore nel caricamento dei Business Registry');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearch(value);
    setPage(0);
    const term = value.trim();
    fetchData(term);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openDialog = (mode, row = null) => {
    setDialogMode(mode);
    setEditingRow(row);
    if (mode === 'edit' && row) {
      setForm({
        fiscal_id: row.fiscal_id || '',
        business_name: row.business_name || '',
        email: row.email || '',
        enabled: !!row.enabled,
        auto_renew_subscription: !!row.auto_renew_subscription
      });
    } else {
      setForm({
        fiscal_id: '',
        business_name: '',
        email: '',
        enabled: true,
        auto_renew_subscription: false
      });
    }
    setDialogOpen(true);
  };

  const handleFormChange = (field) => (event) => {
    const value =
      field === 'enabled' || field === 'auto_renew_subscription'
        ? event.target.checked
        : event.target.value;
    setForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      if (!form.fiscal_id || !form.business_name || !form.email) {
        toast.error('CF, ragione sociale ed email sono obbligatori');
        return;
      }

      if (dialogMode === 'create') {
        const res = await api.post('/open-banking/business-registries', form);
        toast.success(res.data?.message || 'Business Registry creato');
      } else if (dialogMode === 'edit' && editingRow) {
        const res = await api.put(
          `/open-banking/business-registries/${editingRow.id}`,
          {
            business_name: form.business_name,
            email: form.email,
            enabled: form.enabled,
            auto_renew_subscription: form.auto_renew_subscription
          }
        );
        toast.success(res.data?.message || 'Business Registry aggiornato');
      }
      setDialogOpen(false);
      fetchData(search.trim());
    } catch (error) {
      console.error('Error saving business registry', error);
      toast.error('Errore durante il salvataggio del Business Registry');
    }
  };

  const handleDelete = async (row) => {
    setRowToDelete(row);
    setConfirmDeleteOpen(true);
  };

  const handleSyncData = async (row) => {
    try {
      const res = await api.post(`/open-banking/business-registries/${row.fiscal_id}/sync`);
      toast.success(res.data?.message || 'Dati azienda sincronizzati');
      fetchData();
    } catch (error) {
      console.error('Error syncing business registry', error);
      toast.error('Errore durante la sincronizzazione dati azienda');
    }
  };

  const handleSyncAccounts = async (row) => {
    try {
      const res = await api.post(
        `/open-banking/business-registries/${row.fiscal_id}/sync-accounts`
      );
      toast.success(res.data?.message || 'Sincronizzazione account completata');
      fetchData();
    } catch (error) {
      console.error('Error syncing business registry accounts', error);
      toast.error('Errore durante la sincronizzazione degli account');
    }
  };

  const handleToggleEnabled = async (row) => {
    const confirmMsg = row.enabled
      ? 'Disabilitare questa azienda per l\'Open Banking?'
      : 'Abilitare questa azienda per l\'Open Banking?';
    if (!window.confirm(confirmMsg)) {
      return;
    }
    try {
      const res = await api.post(
        `/open-banking/business-registries/${row.fiscal_id}/toggle-enabled`
      );
      toast.success(res.data?.message || 'Stato azienda aggiornato');
      fetchData();
    } catch (error) {
      console.error('Error toggling business registry enabled', error);
      toast.error('Errore durante l\'aggiornamento dello stato azienda');
    }
  };

  const filteredRows = rows.filter((row) => {
    if (enabledFilter === 'enabled' && !row.enabled) return false;
    if (enabledFilter === 'disabled' && row.enabled) return false;
    if (autoRenewFilter === 'with' && !row.auto_renew_subscription) return false;
    if (autoRenewFilter === 'without' && row.auto_renew_subscription) return false;
    if (hasAccountsFilter && !(row.accounts_count > 0)) return false;
    return true;
  });

  const paginated = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ObBusinessLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Business Registry
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Anagrafica aziende collegata via Open Banking
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={() => openDialog('create')}
        >
          Nuova azienda
        </Button>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 260 }}>
              <TextField
                fullWidth
                placeholder="Cerca per CF, ragione sociale o email..."
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={20} color="#637381" />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => handleSearchChange({ target: { value: '' } })}
                      >
                        <X size={18} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />
            </Box>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Stato</InputLabel>
              <Select
                label="Stato"
                value={enabledFilter}
                onChange={(e) => {
                  setEnabledFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">Tutti</MenuItem>
                <MenuItem value="enabled">Solo abilitati</MenuItem>
                <MenuItem value="disabled">Solo disabilitati</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Auto rinnovo</InputLabel>
              <Select
                label="Auto rinnovo"
                value={autoRenewFilter}
                onChange={(e) => {
                  setAutoRenewFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">Tutti</MenuItem>
                <MenuItem value="with">Con auto rinnovo</MenuItem>
                <MenuItem value="without">Senza auto rinnovo</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel>Con account</InputLabel>
              <Select
                label="Con account"
                value={hasAccountsFilter ? 'yes' : 'no'}
                onChange={(e) => {
                  setHasAccountsFilter(e.target.value === 'yes');
                  setPage(0);
                }}
              >
                <MenuItem value="no">Tutti</MenuItem>
                <MenuItem value="yes">Solo con account</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>CF Azienda</TableCell>
                <TableCell>Ragione Sociale</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Abilitato</TableCell>
                <TableCell>Auto Renew</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Creato il</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nessuna azienda registrata.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.fiscal_id}</TableCell>
                    <TableCell>{row.business_name}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.enabled ? 'Abilitata' : 'Disabilitata'}
                        color={row.enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.auto_renew_subscription ? 'Attivo' : 'Non attivo'}
                        color={row.auto_renew_subscription ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.accounts_count || 0}
                        size="small"
                        color={row.accounts_count > 0 ? 'info' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {row.created_at
                        ? new Date(row.created_at).toLocaleDateString('it-IT')
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleSyncData(row)}
                          title="Sincronizza dati azienda"
                          sx={{
                            borderRadius: 1.5,
                            bgcolor: '#f3f4f6',
                            '&:hover': { bgcolor: '#e5e7eb' },
                            color: '#4b5563',
                            border: '1px solid #e5e7eb'
                          }}
                        >
                          <RefreshCcw size={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleSyncAccounts(row)}
                          title="Sincronizza account"
                          sx={{
                            borderRadius: 1.5,
                            bgcolor: '#f3f4f6',
                            '&:hover': { bgcolor: '#e5e7eb' },
                            color: '#4b5563',
                            border: '1px solid #e5e7eb'
                          }}
                        >
                          <RefreshCw size={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleEnabled(row)}
                          title={row.enabled ? 'Disabilita' : 'Abilita'}
                          sx={{
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: row.enabled ? '#fee2e2' : '#dcfce7',
                            bgcolor: '#ffffff',
                            '&:hover': { bgcolor: '#f9fafb' },
                            color: row.enabled ? '#dc2626' : '#16a34a'
                          }}
                        >
                          <Power size={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openDialog('edit', row)}
                          title="Modifica"
                          sx={{ ml: 0.5 }}
                        >
                          <EditIcon size={18} />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(row)}
                          title="Elimina"
                        >
                          <DeleteIcon size={18} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {dialogMode === 'create' ? 'Nuova azienda Open Banking' : 'Modifica Business Registry'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="CF azienda"
              value={form.fiscal_id}
              onChange={handleFormChange('fiscal_id')}
              disabled={dialogMode === 'edit'}
              size="small"
              fullWidth
            />
            <TextField
              label="Ragione sociale"
              value={form.business_name}
              onChange={handleFormChange('business_name')}
              size="small"
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={handleFormChange('email')}
              size="small"
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.enabled}
                    onChange={handleFormChange('enabled')}
                    color="success"
                  />
                }
                label="Abilitata"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.auto_renew_subscription}
                    onChange={handleFormChange('auto_renew_subscription')}
                    color="primary"
                  />
                }
                label="Auto rinnovo"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleSave}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => {
          setConfirmDeleteOpen(false);
          setRowToDelete(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Conferma eliminazione Business Registry</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Stai per eliminare il Business Registry:
          </Typography>
          {rowToDelete && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {rowToDelete.business_name || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CF azienda: {rowToDelete.fiscal_id || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Email: {rowToDelete.email || '-'}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary">
            L&apos;eliminazione rimuove questo Business Registry dall&apos;area Open Banking,
            ma non cancella automaticamente gli account o le transazioni collegate.
            Potrai ricrearlo in seguito se necessario.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmDeleteOpen(false);
              setRowToDelete(null);
            }}
          >
            Annulla
          </Button>
          <Button variant="contained" color="error" onClick={async () => {
            if (!rowToDelete) {
              setConfirmDeleteOpen(false);
              return;
            }
            try {
              const res = await api.delete(`/open-banking/business-registries/${rowToDelete.id}`);
              toast.success(res.data?.message || 'Business Registry eliminato');
              setConfirmDeleteOpen(false);
              setRowToDelete(null);
              fetchData(search.trim());
            } catch (error) {
              console.error('Error deleting business registry', error);
              toast.error('Errore durante l\'eliminazione del Business Registry');
            }
          }}>
            Conferma eliminazione
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
